import type { EvidenceGrade, WeeklyHolidayResult, WeeklyHolidayWeek } from './types.js';

/**
 * 주휴수당은 소정근로시간·소정근로일을 기준으로 계산한다.
 * 분 단위 중간 반올림을 하지 않고 정산기간 합계에서 한 번만 원 단위로 반올림한다.
 */
export function calculateWeeklyHoliday(input: WeeklyHolidayWeek): WeeklyHolidayResult {
  if (!Number.isInteger(input.weeklyScheduledMinutes) || input.weeklyScheduledMinutes < 0 || input.weeklyScheduledMinutes > 2400) {
    throw new Error('INVALID_SCHEDULED_HOURS');
  }
  if (!Number.isInteger(input.scheduledDaysPerWeek) || input.scheduledDaysPerWeek < 0 || input.scheduledDaysPerWeek > 7) {
    throw new Error('INVALID_SCHEDULED_DAYS');
  }
  if (!Number.isInteger(input.ordinaryHourlyRate) || input.ordinaryHourlyRate <= 0) throw new Error('INVALID_HOURLY_RATE');
  const base = { weekStart: input.weekStart, holidayDate: input.holidayDate, divisor: null, paidMinutes: 0, rawAmount: 0, amount: 0, grade: 'A' as EvidenceGrade, reason: null as WeeklyHolidayResult['reason'] };
  if (input.includesWeeklyHolidayPay) return { ...base, grade: 'A', reason: null };
  if (input.weeklyScheduledMinutes < 900) return { ...base, grade: input.attendanceKnown === false ? 'B' : 'A', reason: 'UNDER_15H' };
  if (input.workedShiftCount === 0) return { ...base, grade: input.attendanceKnown === false ? 'B' : 'A', reason: 'NO_WORK' };
  if (input.absence) return { ...base, grade: input.attendanceKnown === false ? 'B' : 'A', reason: 'ABSENCE' };
  const divisor = input.scheduledDaysPerWeek >= 5 ? input.scheduledDaysPerWeek : 5;
  const paidMinutes = input.weeklyScheduledMinutes / divisor;
  const rawAmount = (paidMinutes * input.ordinaryHourlyRate) / 60;
  // 퇴직 주의 존속 여부를 확인할 수 없으면 범위·차액을 확정하지 않는다.
  const grade: EvidenceGrade = input.relationshipContinues === false ? 'B' : input.attendanceKnown === false ? 'B' : 'A';
  return { ...base, divisor, paidMinutes, rawAmount, amount: rawAmount, grade };
}

export function calculateWeeklyHolidayPeriod(inputs: WeeklyHolidayWeek[]): { weeks: WeeklyHolidayResult[]; amount: number; grade: EvidenceGrade } {
  const weeks = inputs.map(calculateWeeklyHoliday);
  const amount = Math.round(weeks.reduce((sum, week) => sum + week.rawAmount, 0));
  const grade = weeks.reduce<EvidenceGrade>((current, week) => {
    const order: EvidenceGrade[] = ['A', 'B', 'C'];
    return order.indexOf(week.grade) > order.indexOf(current) ? week.grade : current;
  }, 'A');
  return { weeks, amount, grade };
}
